import type { FocusEvent, HTMLAttributes, KeyboardEvent } from 'react';

import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CloseIcon } from '../../icons';
import { classNames } from '../../utils/class-names';
import { IconButton } from '../buttons';

import styles from './chip.module.css';

export type InputChipProps = {
  value: string;
  disabled?: boolean;
  invalid?: boolean;
  onEdit?: (value: string) => void;
  onDelete?: () => void;
} & HTMLAttributes<HTMLDivElement>;

/**
 * @param {string} value - The value of the chip.
 * @param {boolean} [disabled] - Whether the chip is disabled or not.
 * @param {boolean} [invalid] - Whether the chip is invalid or not.
 * @param {Function} [onEdit] - Optional function which is called when the chip is edited.
 * @param {Function} [onDelete] - Optional function which is called when the delete button of the chip is clicked.
 */
export function InputChip({ value, disabled, invalid, className, onEdit, onDelete, ...props }: InputChipProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLSpanElement>(null);
  const [isEditing, setIsEditing] = useState(false);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!inputRef.current) return;

    if (event.key === 'Enter') {
      inputRef.current.blur();
    }

    if (event.key === 'Escape') {
      inputRef.current.textContent = value;
      inputRef.current.blur();
    }
  };

  const handleFocus = () => {
    setIsEditing(true);
  };

  const handleBlur = (event: FocusEvent<HTMLSpanElement>) => {
    setIsEditing(false);

    const newValue = event.target.textContent?.trim() || '';

    if (!inputRef.current || newValue === value) return;
    if (!newValue) return onDelete?.();
    onEdit?.(newValue);
  };

  return (
    <div
      data-testid={`chip-${value}`}
      aria-disabled={disabled}
      className={classNames(
        styles.chip,
        styles.input,
        invalid && !isEditing && styles.invalid,
        isEditing && styles.editing,
        className
      )}
      {...props}
    >
      <span
        ref={inputRef}
        data-testid="chip-value"
        className={classNames(styles.value, onEdit && styles.editable)}
        contentEditable={onEdit && !disabled}
        suppressContentEditableWarning
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
      >
        {value}
      </span>

      {onDelete && (
        <IconButton
          rounded
          data-testid="chip-delete-button"
          aria-label={t('chip.deleteButton')}
          title={t('chip.deleteButton')}
          disabled={disabled}
          onClick={onDelete}
        >
          <CloseIcon />
        </IconButton>
      )}
    </div>
  );
}
